/**
 * Native Shoes — Swatch Shared (cache + fetch, PDP + collection grid)
 * ────────────────────────────────────────────────────────────────────────────
 * Author:   Anton Siedlecki
 * Version:  1.7.0
 * Date:     2026-10-06
 *
 * v1.7.0 — Sibling swatch discounts were always empty: the Storefront token
 * cannot read the Online-Store-only tier collections (collection(handle)
 * returns null, product.collections omits them). fetchActiveDiscountMap()
 * now makes one same-origin request to templates/collection.sale-map.liquid
 * (all tiers, base64 JSON, paged past 50 products) and shares the result
 * across every card. See .projects/features/300-anniversary-sale-discounts.
 *
 * v1.6.0 — URGENT live regression fix. The Anniversary Sale work earlier
 * today (see the 2026-10-06 entry on fetchStyleProductsPage()'s query
 * string, and on nextPage() below) added `collections(first: 50) {
 * nodes { id handle metafields(identifiers: [...5 discount fields]) {
 * key value } } }` per sibling product. With up to 250 siblings in
 * `references(first: 250)`, that's up to 250 x 50 x 5 = 62,500 field
 * resolutions in ONE query — almost certainly exceeding the Storefront
 * API's query-cost budget. Live, reported symptom: collection-page
 * swatches sitewide showing only the current color (no sibling row at
 * all) and noticeably slower collection/product pages. Root cause: this
 * query returning a GraphQL `errors` array with no usable `data`,
 * silently falling back to "just the current color" with zero console
 * signal (nextPage() treated a cost-budget error identically to "style
 * not found" — fixed alongside this, see nextPage()'s own comment).
 * Fixed by reverting `collections` back down to `{ first: 10 } { nodes {
 * handle } }` — no metafields sub-query at all — and moving the discount
 * lookup to a tiny, Liquid-computed "active discount tiers" table
 * (handle/pct/name for the 4 known tier collections,
 * resolved ONCE and cheaply server-side) that assets/native-collection-
 * swatches.js now checks sibling collection HANDLES against instead.
 * See that file's own changelog and snippets/native-collection-
 * swatches.liquid's data-active-discount-tiers attribute.
 *
 * v1.5.0 — Real, live-reported bug: STYLE_CACHE_PREFIX was never
 * bumped across v1.2.0-v1.4.0's several superset-shape additions
 * (tags, badgesMetafield, price/compareAtPrice) — a browser with an
 * existing cache entry (from before today, or mid-session between
 * those changes) kept serving data missing those fields for its
 * remaining 24h TTL, silently making hasCompareAtPrice compute false
 * with nothing to compute FROM. Reported live on Apollo — two colors
 * both genuinely on sale ($125, was $155), Sale badge disappearing.
 * Bumped v3 -> v4, same fix as v1.1.0's own bump for the identical
 * class of bug.
 *
 * v1.4.0 — Real, live-confirmed bug: variants(first:100).nodes[0] is
 * NOT the same thing as Shopify's own product.price. Confirmed live on
 * a real price-varying product (Robbie Kids toddler/kid sizes at two
 * different price points): variants[0] was $60, but product.price is
 * $55 — Shopify's documented "price of the product's CHEAPEST variant,"
 * not literally the first one returned. Tried switching to the Product
 * type's own priceRange/compareAtPriceRange fields (a cleaner-looking
 * fix), but reverted after confirming LIVE that pairing priceRange.
 * minVariantPrice with compareAtPriceRange.maxVariantPrice can itself
 * produce a FALSE "on sale" signal whenever price simply varies by
 * size — the min and max of those two ranges aren't guaranteed to come
 * from the same variant (confirmed: on this exact product, every
 * variant's own compareAtPrice mirrors its own price exactly — no real
 * markdown anywhere — yet the two ranges alone would suggest a $55-vs-
 * $60 discount that doesn't exist). variants[].price/compareAtPrice
 * stays, unchanged in shape from before this whole detour — the actual
 * fix is entirely in HOW the consuming JS picks a variant now (see
 * assets/native-collection-swatches.js's v3.4.0 changelog): find the
 * genuinely cheapest variant, then use THAT SAME variant's own price
 * and compareAtPrice together, never mixing aggregates across variants.
 *
 * v1.3.0 — fetchStyleProductsPage() now sends @inContext(country:
 * $country), matching the pattern already used elsewhere in this
 * project (assets/native-collection-swatches.js's live-availability
 * query, assets/native-product-cross-links.js's own fetches). This
 * query ran with NO market context at all until now — harmless while it
 * only fetched currency-agnostic fields, but a real bug now that v1.2.0
 * added price/compareAtPrice: confirmed live that this store defaults
 * to CAD, and the un-scoped query was returning USD-tier numbers for a
 * sibling's price while the current product's own price (Liquid-
 * rendered) correctly showed CAD — a real, visible price mismatch the
 * instant a shopper clicked a sibling swatch. STYLE_CACHE_PREFIX's
 * cache key already included COUNTRY_CODE from the start; the fetch
 * itself just never actually used a country context until now.
 *
 * v1.2.0 — Added tags, badgesMetafield (custom.badges), and per-variant
 * price + compareAtPrice to the superset shape, for assets/native-
 * collection-swatches.js's new dynamic-badging fix (a clicked sibling
 * swatch used to leave the card's Sale/tag/metafield badges showing the
 * ORIGINAL server-rendered color's badges, not the one actually being
 * previewed — only a few colors in a given consolidated_style are
 * usually really on sale). variants[].price was added alongside
 * compareAtPrice after a real, live-confirmed bug: a bare "does
 * compareAtPrice exist" check is wrong — Shopify's own product.
 * compare_at_price in Liquid can be truthy with zero real markdown, and
 * this project's own snippets/price.liquid has always used a genuine
 * compare_at_price > price comparison for exactly that reason. The
 * shared shape now carries both fields so consumers can do the same
 * real comparison instead of a truthy check. Per the CRITICAL RULE
 * below, native-product-cross-links.js (PDP) now receives these four
 * fields too even though its own swatch UI has no badge display — it
 * simply ignores them, same as it already ignores fields it doesn't use
 * elsewhere in this shape.
 *
 * v1.1.0 (2026-08-27) — Bumped STYLE_CACHE_PREFIX to force one clean
 * refetch store-wide after a bulk Admin API write flipped
 * custom.image_drop_shadow on 2,630 products directly — the version probe
 * only watches a style's updatedAt + sibling handle set, neither of which
 * a metafield write on an existing sibling changes, so previously-cached
 * rows would otherwise have silently served stale (pre-write) drop-shadow
 * flags for up to their remaining 24h TTL. See STYLE_CACHE_PREFIX's own
 * comment.
 *
 * Purpose:  A consolidated_style's sibling list — each sibling's handle,
 *           color code, color group, and color image/hex/name — is the
 *           same underlying data whether a shopper is looking at a
 *           collection card or a PDP. Until today, assets/native-
 *           collection-swatches.js and assets/native-product-cross-
 *           links.js each fetched and cached it independently, under
 *           separate localStorage namespaces, with near-duplicate cache
 *           logic (each file's own comments cross-reference the other's
 *           implementation as "the same pattern"). This file is that
 *           logic, extracted once, so a visit to either page type warms
 *           the SAME cache entry the other reads — cache_shared here
 *           after both files independently maintained day-long caches
 *           for the exact same styles all catalog day long.
 *
 *           Explicit user framing for this extraction: cache the
 *           AWARENESS of what's in a consolidated_style (its roster —
 *           handles, colors, groups, images), and let each feature's
 *           own always-live availability check net against that cached
 *           roster at render time. That's exactly the split below:
 *           this file owns the roster cache; it owns NO availability
 *           data or availability logic at all — that stays in each
 *           consumer, because PDP and collection grid need genuinely
 *           different granularity (product-level vs per-variant). Both
 *           now share the same "never show a sold-out/unavailable
 *           swatch, not even briefly" discipline — PDP originally
 *           accepted a brief-then-self-correcting staleness window as a
 *           lower-stakes tradeoff, but that was overridden by explicit
 *           user instruction on 2026-08-28 ("they should never flash")
 *           after a real, live case (Robbie Dash Kids) showed a
 *           market-unpublished sibling flashing visible before
 *           disappearing. See native-product-cross-links.js's own
 *           changelog for the one placed where availability-shaped data
 *           IS present in the cached roster, and why.
 *
 * CRITICAL RULE — do not violate this: every caller must always request
 * this file's one canonical superset of fields, never a subset. This
 * project hit a real, live, user-reported bug on 2026-08-07 from
 * exactly this mistake once already: two features briefly shared one
 * cache key with two DIFFERENT data shapes, and whichever one wrote the
 * cache first "won," silently starving the other of fields it needed
 * (a PDP page that happened to read a collection-grid-authored cache
 * entry found every product's color group undefined and rendered
 * everything ungrouped). The fix then was separating the namespaces;
 * the fix now that they're being intentionally merged again is that
 * BOTH features fetch the exact same shape, every time, via this one
 * function — no caller-specific query variants.
 *
 * The one field that looks like it belongs to "availability" but is
 * included anyway: variants(first:100){availableForSale}. Collection
 * grid ignores this field completely — it has its own always-live,
 * render-blocking availability check (fetchLiveAvailability() in its
 * own file) and never reads anything availability-shaped from this
 * cache. PDP now also blocks its first render on its own live per-size
 * fetch (as of 2026-08-28, see native-product-cross-links.js's boot()),
 * so in normal operation this field is dead weight — kept only as a
 * defensive fallback for isFullyOOS() in case a handle is ever rendered
 * without a matching live-data entry, not as a real first-paint source
 * of truth anymore.
 *
 * Usage:
 *   window.NativeShops.swatchShared.getStyleIdentity(domain, token, styleHandle)
 *     .then(function (identity) {
 *       // identity = { products: [...], swatchMap: {...}, handlesHash } or null
 *     });
 *   window.NativeShops.swatchShared.fetchSwatches(domain, token, colorCodes)
 *     .then(function (swatchMap) { ... }); // for ad-hoc single/few-color needs
 *   window.NativeShops.swatchShared.gql(domain, token, query, variables);
 *     // exposed so each consumer's OWN live-availability query (product-
 *     // level for collection grid, per-variant for PDP) doesn't need its
 *     // own duplicate fetch() wrapper either.
 */
(function () {
  'use strict';

  window.NativeShops = window.NativeShops || {};

  function debugEnabled() {
    try {
      if (new URLSearchParams(window.location.search).get('native_swatch_shared_debug') === '1') return true;
      if (window.localStorage && window.localStorage.getItem('native_swatch_shared_debug') === '1') return true;
    } catch (e) {}
    return false;
  }

  var DEBUG = debugEnabled();
  function log() { if (!DEBUG) return; console.log.apply(console, ['[swatch-shared]'].concat(Array.prototype.slice.call(arguments))); }
  function err() { console.error.apply(console, ['[swatch-shared]'].concat(Array.prototype.slice.call(arguments))); }

  // v2 (2026-08-27): bumped after a one-time bulk Admin API write flipped
  // custom.image_drop_shadow on 2,630 products directly — a change the
  // version-probe below has no way to detect (it only watches a style's
  // updatedAt + sibling handle set, neither of which a metafield write on
  // an existing sibling touches). Bumping the prefix orphans every
  // previously-cached row store-wide, forcing one clean refetch instead of
  // silently serving stale drop-shadow flags for up to the old cache's
  // remaining 24h TTL. Only needed this once, for this one out-of-band
  // change — the version probe is the normal path for everything else.
  // v3 (2026-08-28): bumped again, right after fixing the actual root
  // cause (custom.image_drop_shadow's metafield definition had
  // access.storefront: NONE — every Storefront API fetch of this field
  // returned null regardless of the real value, until fixed via
  // metafieldDefinitionUpdate). Any cache row written under -v2 between
  // that first cache bump and this metafield-access fix would still carry
  // a null drop-shadow flag for its full 24h TTL despite the underlying
  // data now being correct — this bump guarantees a clean slate with no
  // such window.
  // Native Shoes (2026-08-31): bumped v3 -> v4 — today's badge/price
  // fixes added several new fields to the superset shape (tags,
  // badgesMetafield, and eventually variants[].price/compareAtPrice)
  // across multiple versions without ever bumping this, so any browser
  // holding a cache entry from before (or even mid-session, between
  // those incremental additions) would keep serving data missing those
  // fields for up to its remaining 24h TTL — hasCompareAtPrice silently
  // computes false with no price/compareAtPrice present at all, which
  // is exactly the live-reported symptom (Apollo: both colors genuinely
  // on sale, badge disappears). Same fix, same reasoning as the v1.1.0
  // bump above for the exact same class of bug.
  // Native Shoes (2026-10-06): bumped v4 -> v5 — the Anniversary Sale
  // work added collections{...discount_* metafields} to the superset
  // shape below. Same reasoning as the v3 -> v4 bump: without a bump,
  // any browser holding a v4 entry would keep serving sibling data with
  // no collections field at all for up to 24h, and a swatch click would
  // silently compute "no discount" for a color that genuinely has one.
  //
  // Native Shoes (2026-10-06, same day): bumped v5 -> v6 — the SAME class
  // of bug, caught live a second time. The collections field's cap moved
  // metafields -> first:10 -> first:50 across three edits this session,
  // but only the FIRST of those (adding the field at all) bumped the
  // version. A browser that cached a v5 entry during the first:10 window
  // kept serving that truncated sibling-collections list for its full 24h
  // TTL even after the live query moved to first:50 — confirmed live:
  // Chase Kids' Chameleon Pink sibling (30% tier) still showed no badge
  // after the first:50 fix was pushed, purely because of this stale cache,
  // not a code bug. Lesson for next time: ANY change to this superset
  // shape's CONTENT (not just which fields exist, but how much of a field
  // is fetched) needs a version bump — "did the field name change" is the
  // wrong test, "could a cached row now contain meaningfully different
  // data than a fresh fetch would" is the right one.
  var STYLE_CACHE_PREFIX  = 'native-swatch-shared-style-v7';
  var SWATCH_CACHE_PREFIX = 'native-swatch-shared-swatch';
  var CACHE_TTL_MS = 24 * 60 * 60 * 1000;
  var API_VERSION = '2026-04';
  var COUNTRY_CODE = (window.NativeShops.config && window.NativeShops.config.countryCode) || 'ZZ';

  // Per-page-load memo of in-flight/completed background revalidation
  // checks, keyed by domain:styleHandle:country, so multiple cards/callers
  // for the same style share one probe instead of each firing their own.
  var revalidationPromises = {};

  function gql(domain, token, query, variables) {
    var body = { query: query };
    if (variables) body.variables = variables;
    return fetch('https://' + domain + '/api/' + API_VERSION + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  // ─── Style identity cache — 24h, localStorage, version-validated ─────────
  function readStyleCache(domain, styleHandle) {
    try {
      if (!window.localStorage) return null;
      var prefix = [STYLE_CACHE_PREFIX, domain, styleHandle, COUNTRY_CODE].join(':');
      var keys = [];
      for (var i = 0; i < window.localStorage.length; i++) {
        var k = window.localStorage.key(i);
        if (k && k.indexOf(prefix) === 0) keys.push(k);
      }
      var hit = null;
      keys.forEach(function (k) {
        var raw = window.localStorage.getItem(k);
        if (!raw) return;
        var parsed;
        try { parsed = JSON.parse(raw); } catch (e) { window.localStorage.removeItem(k); return; }
        if (!parsed || parsed.expiresAt <= Date.now() ||
            !parsed.data || !Array.isArray(parsed.data.products)) {
          window.localStorage.removeItem(k);
          return;
        }
        if (!hit) { log('style cache hit', k); hit = parsed; }
      });
      return hit;
    } catch (e) {}
    return null;
  }

  function writeStyleCache(domain, styleHandle, version, updatedAt, data) {
    try {
      if (!window.localStorage) return;
      var prefix = [STYLE_CACHE_PREFIX, domain, styleHandle, COUNTRY_CODE].join(':');
      for (var i = window.localStorage.length - 1; i >= 0; i--) {
        var k = window.localStorage.key(i);
        if (k && k.indexOf(prefix) === 0) window.localStorage.removeItem(k);
      }
      var key = [prefix, version || 'no-version'].join(':');
      window.localStorage.setItem(key, JSON.stringify({
        expiresAt: Date.now() + CACHE_TTL_MS,
        version: version || 'no-version',
        updatedAt: updatedAt || '',
        data: data
      }));
      log('style cache stored', key);
    } catch (e) {}
  }

  // Lightweight background probe — updatedAt + the sibling handle set, no
  // images/metafields — detects a backend assortment change or a sibling's
  // own handle changing without paying for a full refetch on every load.
  function checkStyleVersion(domain, token, styleHandle) {
    var query =
      'query NativeSwatchSharedVersion($styleHandle: String!) {' +
      '  metaobject(handle: { handle: $styleHandle, type: "consolidated_style" }) {' +
      '    updatedAt' +
      '    field(key: "style_colors") {' +
      '      references(first: 250) { nodes { ... on Product { handle } } }' +
      '    }' +
      '  }' +
      '}';
    return gql(domain, token, query, { styleHandle: styleHandle }).then(function (data) {
      var mo = data && data.data && data.data.metaobject;
      if (!mo) return null;
      var refs = mo.field && mo.field.references && mo.field.references.nodes;
      var handles = (refs || []).map(function (n) { return n.handle; }).sort();
      return { updatedAt: mo.updatedAt || null, handlesHash: handles.join('|') };
    }).catch(function () { return null; });
  }

  // Fires at most one version probe per style+country per page load. Only
  // used to refresh the CACHE for the NEXT page load (whichever feature
  // happens to read it) — never blocks or re-renders the CURRENT one. A
  // failed probe fails open (leaves the existing cache alone).
  function revalidateStyleInBackground(domain, token, styleHandle, cachedUpdatedAt, cachedHandlesHash, refetchFn) {
    var probeKey = [domain, styleHandle, COUNTRY_CODE].join(':');
    if (revalidationPromises[probeKey]) return;
    revalidationPromises[probeKey] = checkStyleVersion(domain, token, styleHandle).then(function (fresh) {
      if (!fresh) return;
      var changed = fresh.updatedAt !== cachedUpdatedAt || fresh.handlesHash !== cachedHandlesHash;
      if (!changed) return;
      log('backend assortment change detected for style', styleHandle, '— refreshing cache for next load');
      return refetchFn();
    }).catch(function () {});
  }

  // ─── Swatch (color) cache — 24h, localStorage, flat TTL, no version probe.
  // Swatch appearance (image/hex/name) essentially never changes and isn't
  // safety-critical if briefly stale — a simple TTL is enough. Keyed by
  // color code alone (not by style), since the same code (e.g. "1105") is
  // shared across many unrelated products/styles across the whole catalog —
  // the highest-value part of this whole extraction to share. ─────────────
  function readSwatchCache(colorCode) {
    try {
      if (!window.localStorage) return null;
      var raw = window.localStorage.getItem(SWATCH_CACHE_PREFIX + ':' + colorCode);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || parsed.expiresAt <= Date.now()) {
        window.localStorage.removeItem(SWATCH_CACHE_PREFIX + ':' + colorCode);
        return null;
      }
      return parsed.swatch;
    } catch (e) { return null; }
  }

  function writeSwatchCache(colorCode, swatch) {
    try {
      if (!window.localStorage) return;
      window.localStorage.setItem(SWATCH_CACHE_PREFIX + ':' + colorCode, JSON.stringify({
        expiresAt: Date.now() + CACHE_TTL_MS,
        swatch: swatch
      }));
    } catch (e) {}
  }

  function fetchSwatches(domain, token, colorCodes) {
    var need = [];
    var map = {};
    colorCodes.forEach(function (c) {
      var cached = readSwatchCache(c);
      if (cached) { map[c] = cached; } else { need.push(c); }
    });
    if (!need.length) return Promise.resolve(map);

    var fields = need.map(function (h, i) {
      return 's' + i + ': metaobject(handle: { handle: "' + h + '", type: "color_swatch" }) {' +
        '  color_name: field(key: "color_name") { value }' +
        '  color_hex: field(key: "color_hex") { value }' +
        '  color_image: field(key: "color_image") { reference { ... on MediaImage { image { url(transform: { maxWidth: 60 }) altText } } } }' +
        '}';
    }).join(' ');

    return gql(domain, token, '{ ' + fields + ' }').then(function (data) {
      need.forEach(function (h, i) {
        var swatch = (data && data.data && data.data['s' + i]) || null;
        map[h] = swatch;
        writeSwatchCache(h, swatch);
      });
      return map;
    }).catch(function (e) { err('fetchSwatches error', e); return map; });
  }

  // ─── Style identity fetch — the ONE canonical superset shape. See the
  // file-level CRITICAL RULE comment above before adding or removing any
  // field here: every consumer gets exactly this shape, always. ──────────
  function fetchStyleProductsPage(domain, token, styleHandle, cursor) {
    var after = cursor ? ', after: "' + cursor + '"' : '';
    // Native Shoes (2026-08-31): @inContext(country: $country) added
    // alongside the price/compareAtPrice fields above — this query ran
    // with no market context at all until now, which was harmless while
    // it only fetched currency-agnostic fields (images, color codes,
    // booleans) but would silently return the WRONG market's price for
    // a shopper outside that default market once real money fields were
    // added (confirmed live: this store defaults to CAD, an un-scoped
    // query returned USD-tier numbers). The STYLE_CACHE_PREFIX cache key
    // already included COUNTRY_CODE from the start (see readStyleCache/
    // writeStyleCache above) — the cache was always ready for this, the
    // fetch just never actually used it until now.
    var query =
      'query NativeSwatchSharedIdentity($styleHandle: String!, $country: CountryCode!) @inContext(country: $country) {' +
      '  metaobject(handle: { handle: $styleHandle, type: "consolidated_style" }) {' +
      '    updatedAt' +
      '    field(key: "style_colors") {' +
      '      references(first: 250' + after + ') {' +
      '        pageInfo { hasNextPage endCursor }' +
      '        nodes {' +
      '          ... on Product {' +
      '            handle' +
      '            onlineStoreUrl' +
      '            featuredImage { url(transform: { maxWidth: 800 }) altText }' +
      '            media(first: 2) { nodes { ... on MediaImage { image { url(transform: { maxWidth: 800 }) altText } } } }' +
      '            colorMetafield: metafield(namespace: "erp", key: "color") { value }' +
      '            colorGroupMetafield: metafield(namespace: "custom", key: "color_group") { value }' +
      '            imageDropShadowMetafield: metafield(namespace: "custom", key: "image_drop_shadow") { value }' +
      '            badgesMetafield: metafield(namespace: "custom", key: "badges") { value }' +
      '            tags' +
      '            variants(first: 100) { nodes { availableForSale price { amount } compareAtPrice { amount } } }' +
      // Native Shoes (2026-10-06, URGENT REVERT): the previous version of
      // this field fetched `metafields(identifiers:[...5 discount fields])`
      // on EVERY sibling's collections(first: 50) — up to 250 siblings x
      // 50 collections x 5 metafields = tens of thousands of field
      // resolutions in one query, almost certainly blowing the Storefront
      // API's query-cost budget. Live symptom: this shared query started
      // returning a GraphQL `errors` array with no usable `data`, which
      // nextPage() (below) treated the same as "metaobject not found" —
      // silently falling back to rendering just the current color with NO
      // siblings at all, sitewide, plus a real cost/latency hit on every
      // page that calls this shared query even when it didn't error.
      // Fixed per explicit instruction: resolve the 4 known Anniversary
      // Sale tier collections' discount data ONCE, cheaply, server-side in
      // Liquid (see snippets/native-collection-swatches.liquid's
      // data-active-discount-tiers attribute) instead of asking GraphQL
      // for metafields on every sibling's collection list. This field now
      // only needs each sibling's collection HANDLES (cheap, no sub-
      // query) so the client can check membership against that small
      // Liquid-built lookup table.
      //
      // 2026-10-06, FINAL FIX: removed this field entirely. first:10 then
      // first:50 both missed the real problem — this field sits INSIDE
      // the references(first:250) sibling loop, so its cost is
      // MULTIPLICATIVE (250 siblings x 50 collections = 12,500 node
      // resolutions, on top of everything else this already-large query
      // fetches), regardless of what the per-sibling cap is. Live
      // confirmed: still breaking (1-swatch fallback) after the 10->50
      // bump. Real fix: per-sibling collection membership is no longer
      // fetched here at all — see fetchActiveDiscountTiers() below, a
      // separate, FLAT (not multiplied by sibling count), one-time-per-
      // page query that fetches the 4 known tier collections' own product
      // lists directly and builds a handle->tier reverse map. Siblings are
      // matched against that map by their own `handle` (already present
      // in this query, no new field needed), not by asking for each
      // sibling's own collection list.
      '          }' +
      '        }' +
      '      }' +
      '    }' +
      '  }' +
      '}';
    return gql(domain, token, query, { styleHandle: styleHandle, country: COUNTRY_CODE });
  }

  // 2026-10-06 (v1.7.0): ONE same-origin fetch of every tier's product
  // handles, shared by every card/swatch on the page (memoized in memory,
  // plus a 5-minute sessionStorage copy so navigation doesn't refetch).
  // Source is templates/collection.sale-map.liquid, NOT the Storefront API:
  // the tier collections are Online-Store-only, so the Storefront token gets
  // collection(handle) === null and the old lookup was always empty — which
  // is why only the server-rendered (current) color ever showed a discount.
  // Returns { productHandle: { pct, name, collectionHandle } }.
  // Window checks use the unix start/end so a cached copy can't outlive a sale.
  // Tiers over 50 products are completed with extra ?page=N requests.
  // keyed by country: the map is market-aware (a tier's Discount markets list), so one market's copy
  // must never be shown after the shopper switches to another
  var SALE_MAP_CACHE_KEY = 'native-sale-map-v2-' + String((window.Shopify && window.Shopify.country) || '');
  var SALE_MAP_TTL_MS = 5 * 60 * 1000;
  var activeDiscountMapPromise = null;

  function saleMapRoot() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  function fetchSaleMapJson(path) {
    return fetch(saleMapRoot() + path, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('sale-map ' + r.status); return r.text(); })
      .then(function (t) {
        var json = window.atob(t.trim());
        try { json = decodeURIComponent(escape(json)); } catch (e) {}
        return JSON.parse(json);
      });
  }

  function loadSaleMapTiers() {
    try {
      var cached = JSON.parse(sessionStorage.getItem(SALE_MAP_CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.t < SALE_MAP_TTL_MS) return Promise.resolve(cached.tiers);
    } catch (e) {}
    return fetchSaleMapJson('collections/all?view=sale-map').then(function (data) {
      var tiers = (data && data.tiers) || [];
      var extra = [];
      tiers.forEach(function (t) {
        var pages = Math.ceil((t.count || 0) / 50);
        for (var pg = 2; pg <= pages; pg++) {
          extra.push(fetchSaleMapJson('collections/' + t.handle + '?view=sale-map&page=' + pg).then(function (d) {
            t.handles = t.handles.concat((d && d.handles) || []);
          }));
        }
      });
      return Promise.all(extra).then(function () {
        try { sessionStorage.setItem(SALE_MAP_CACHE_KEY, JSON.stringify({ t: Date.now(), tiers: tiers })); } catch (e) {}
        return tiers;
      });
    });
  }

  function fetchActiveDiscountMap() {
    if (activeDiscountMapPromise) return activeDiscountMapPromise;
    activeDiscountMapPromise = loadSaleMapTiers().then(function (tiers) {
      var map = {};
      var nowSec = Math.floor(Date.now() / 1000);
      tiers.forEach(function (t) {
        if (!t || !(t.pct > 0)) return;
        if (t.start && nowSec < t.start) return;
        if (t.end && nowSec > t.end) return;
        var tier = { pct: t.pct, name: t.name || 'On sale', collectionHandle: t.handle };
        (t.handles || []).forEach(function (h) {
          var existing = map[h];
          if (!existing || tier.pct > existing.pct) map[h] = tier;
        });
      });
      return map;
    }).catch(function () { return {}; });
    return activeDiscountMapPromise;
  }

  function fetchStyleProducts(domain, token, styleHandle) {
    var all = [];
    var metaUpdatedAt = '';

    function nextPage(cursor) {
      return fetchStyleProductsPage(domain, token, styleHandle, cursor).then(function (data) {
        // Native Shoes (2026-10-06): distinguish a genuine GraphQL error
        // (query-cost budget exceeded, malformed query, etc. — `errors`
        // array present, `data` usually null/absent) from the ordinary
        // "metaobject not found" case (`data.metaobject` is just null,
        // no `errors` at all). Both used to fall into the same silent
        // "no field -> stop, return whatever we have" path below with zero
        // console signal, which is exactly how the Anniversary Sale
        // query-cost regression went undiagnosed — swatches quietly
        // degraded to "current color only" sitewide with no visible
        // error. Still fails the SAME way (never throws, always resolves
        // with whatever's been accumulated so far), just logs loudly first
        // so a future regression like this one is diagnosable from the
        // console instead of looking identical to an empty style.
        if (data && data.errors && data.errors.length) {
          err('GraphQL errors for style', styleHandle, '—', data.errors.map(function (e) { return e.message; }).join('; '));
        }
        var mo = data && data.data && data.data.metaobject;
        var field = mo && mo.field;
        if (!field) {
          err('metaobject/field not found for style:', styleHandle);
          return { products: all, updatedAt: metaUpdatedAt, count: all.length };
        }
        if (!metaUpdatedAt && mo.updatedAt) metaUpdatedAt = mo.updatedAt;
        var refs = field.references;
        var nodes = (refs && refs.nodes) || [];
        all = all.concat(nodes);
        if (refs && refs.pageInfo && refs.pageInfo.hasNextPage) {
          return nextPage(refs.pageInfo.endCursor);
        }
        return { products: all, updatedAt: metaUpdatedAt, count: all.length };
      });
    }

    return nextPage(null);
  }

  function collectColorCodes(products) {
    var seen = {}, codes = [];
    products.forEach(function (p) {
      var c = p.colorMetafield && p.colorMetafield.value;
      if (c && !seen[c]) { seen[c] = true; codes.push(c); }
    });
    return codes;
  }

  // Resolves identity for one style: cache hit -> use it, kick a background
  // probe for NEXT load (doesn't block or re-render this one, and benefits
  // whichever feature — PDP or collection grid — reads it next). Cache
  // miss -> full fetch (products + their swatches), write cache, return
  // fresh. Never throws — a hard failure resolves to null so a caller can
  // skip that style's cards/rendering cleanly instead of hanging the page.
  function getStyleIdentity(domain, token, styleHandle) {
    var cachedRow = readStyleCache(domain, styleHandle);
    if (cachedRow) {
      revalidateStyleInBackground(domain, token, styleHandle, cachedRow.updatedAt, cachedRow.data.handlesHash, function () {
        return fetchStyleProducts(domain, token, styleHandle).then(function (result) {
          return fetchSwatches(domain, token, collectColorCodes(result.products)).then(function (swatchMap) {
            if (!result.products.length) return;
            var handlesHash = result.products.map(function (p) { return p.handle; }).sort().join('|');
            writeStyleCache(domain, styleHandle, (result.updatedAt || '') + '|' + result.count, result.updatedAt, {
              products: result.products, swatchMap: swatchMap, handlesHash: handlesHash
            });
          });
        });
      });
      return Promise.resolve(cachedRow.data);
    }

    return fetchStyleProducts(domain, token, styleHandle).then(function (result) {
      return fetchSwatches(domain, token, collectColorCodes(result.products)).then(function (swatchMap) {
        if (!result.products.length) {
          log('skipping style cache write — 0 products for', styleHandle);
          return null;
        }
        var handlesHash = result.products.map(function (p) { return p.handle; }).sort().join('|');
        var data = { products: result.products, swatchMap: swatchMap, handlesHash: handlesHash };
        writeStyleCache(domain, styleHandle, (result.updatedAt || '') + '|' + result.count, result.updatedAt, data);
        return data;
      });
    }).catch(function (e) {
      err('style identity fetch failed for', styleHandle, e);
      return null;
    });
  }

  window.NativeShops.swatchShared = {
    gql: gql,
    getStyleIdentity: getStyleIdentity,
    fetchSwatches: fetchSwatches,
    fetchActiveDiscountMap: fetchActiveDiscountMap
  };
})();
